import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { Award, Star, TrendingUp, User, Calendar, Edit, Eye, Shield } from "lucide-react";
import { ClientEvaluation } from "@/hooks/useClientEvaluations";
import { ClientEvaluationModal } from "@/components/performance/ClientEvaluationModal";
import { PerformanceBreakdownView } from "@/components/performance/PerformanceBreakdownView";
import { format } from "date-fns";

interface ClientPerformanceViewProps {
  clientId: string;
}

interface GuardPerformance {
  guard_id: string;
  guard_name: string;
  guard_avatar?: string;
  final_score: number;
  base_score: number;
  performance_grade: string;
  team_id?: string;
  team_name?: string;
  hasEvaluation: boolean;
  canEvaluate: boolean;
  currentEvaluation?: ClientEvaluation;
}

export const ClientPerformanceView = ({ clientId }: ClientPerformanceViewProps) => {
  const { toast } = useToast();
  const [selectedGuard, setSelectedGuard] = useState<{ guardId: string; teamId: string; guardName: string; guardAvatar?: string } | null>(null);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [showPerformanceDetails, setShowPerformanceDetails] = useState<string | null>(null);
  
  // Fetch guards assigned to this client
  const { data: assignedGuards, isLoading, refetch } = useQuery({
    queryKey: ['client-assigned-guards', clientId],
    queryFn: async (): Promise<GuardPerformance[]> => {
      console.log('Starting performance query for clientId:', clientId);
      
      // Get teams assigned to this client
      const { data: clientTeams, error: teamsError } = await supabase
        .from('client_teams')
        .select(`
          team_id,
          teams!inner (
            id,
            name
          )
        `)
        .eq('client_id', clientId);

      console.log('Client teams query result:', { clientTeams, teamsError });

      if (teamsError) throw teamsError;

      if (!clientTeams || clientTeams.length === 0) {
        console.log('No teams found for client');
        return [];
      }

      const teamIds = clientTeams.map(ct => ct.team_id);
      console.log('Team IDs for client:', teamIds);
      
      // Get all team members first
      console.log('Fetching team members for teamIds:', teamIds);
      const { data: teamMembers, error: membersError } = await supabase
        .from('team_members')
        .select(`
          profile_id,
          team_id,
          profiles!inner (
            id,
            first_name,
            last_name,
            full_name,
            avatar_url
          ),
          teams!inner (
            id,
            name
          )
        `)
        .in('team_id', teamIds);

      if (membersError) {
        console.error('Error fetching team members:', membersError);
        throw membersError;
      }

      console.log('Found team members:', teamMembers?.length || 0, teamMembers);

      // Filter to include only guards (users who either have 'guard' role or no admin/manager/client roles)
      const filteredMembers = [];
      
      for (const member of teamMembers || []) {
        console.log('Checking member:', member.profiles.full_name, 'ID:', member.profile_id);
        
        // Get role from profiles table instead of user_roles
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('Role')
          .eq('id', member.profile_id)
          .single();

        if (profileError) {
          console.error('Error fetching profile role for', member.profile_id, ':', profileError);
          continue;
        }

        const userRole = profileData?.Role;
        console.log('User role for', member.profiles.full_name, ':', userRole);
        
        // Only include users who have the 'guard' role
        const isGuard = userRole === 'guard';
        
        console.log('Role check for', member.profiles.full_name, '- isGuard:', isGuard, 'role:', userRole);

        if (isGuard) {
          console.log('Including member:', member.profiles.full_name);
          filteredMembers.push(member);
        } else {
          console.log('Excluding member:', member.profiles.full_name, 'with role:', userRole);
        }
      }

      console.log('Filtered members count:', filteredMembers.length);

      const guardPerformances: GuardPerformance[] = [];

      // For each guard, get their performance data and evaluation status
      for (const member of filteredMembers) {
        const guardId = member.profile_id;
        const guardName = member.profiles.full_name || 
          `${member.profiles.first_name || ''} ${member.profiles.last_name || ''}`.trim();

        // Get performance data for the last 6 months
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 6);

          try {
            // Check if client can evaluate this guard for current semester
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const currentSemester = currentDate.getMonth() >= 6 ? 'H2' : 'H1';

            const { data: existingEvaluation } = await supabase
              .from('client_evaluations')
              .select('*')
              .eq('guard_id', guardId)
              .eq('client_id', clientId)
              .eq('evaluation_year', currentYear)
              .eq('evaluation_period', currentSemester)
              .maybeSingle();

            // Get performance data for the current semester
            const semesterStartDate = new Date(currentYear, currentSemester === 'H1' ? 0 : 6, 1);
            const semesterEndDate = new Date(currentYear, currentSemester === 'H1' ? 5 : 11, 31);

            const { data: performanceData } = await supabase.rpc(
              'calculate_guard_performance_with_base_score',
              {
                p_guard_id: guardId,
                p_start_date: semesterStartDate.toISOString().split('T')[0],
                p_end_date: semesterEndDate.toISOString().split('T')[0]
              }
            );

            const performance = performanceData?.[0];
            
            // Use the correct final_score from the backend calculation
            const finalScoreWithClientEval = performance?.final_score || performance?.base_score || 0;

            guardPerformances.push({
              guard_id: guardId,
              guard_name: guardName,
              guard_avatar: member.profiles.avatar_url,
              final_score: finalScoreWithClientEval,
              base_score: performance?.base_score || 0,
              performance_grade: performance?.performance_grade || 'N/A',
              team_id: member.team_id,
              team_name: member.teams.name,
              hasEvaluation: !!existingEvaluation,
              canEvaluate: true, // Client can always evaluate within the current semester
              currentEvaluation: existingEvaluation || undefined
            });
        } catch (error) {
          console.error(`Error fetching performance for guard ${guardId}:`, error);
          guardPerformances.push({
            guard_id: guardId,
            guard_name: guardName,
            guard_avatar: member.profiles.avatar_url,
            final_score: 0,
            base_score: 0,
            performance_grade: 'N/A',
            team_id: member.team_id,
            team_name: member.teams.name,
            hasEvaluation: false,
            canEvaluate: true
          });
        }
      }

      return guardPerformances;
    },
    enabled: !!clientId,
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000, // 30 seconds
  });

  const handleEvaluateGuard = (guardId: string, teamId: string) => {
    const guardData = assignedGuards?.find(g => g.guard_id === guardId);
    if (guardData) {
      setSelectedGuard({ 
        guardId, 
        teamId, 
        guardName: guardData.guard_name,
        guardAvatar: guardData.guard_avatar
      });
      setShowEvaluationModal(true);
    }
  };

  const handleEvaluationComplete = () => {
    // Refetch data after evaluation submission/update
    refetch();
    setShowEvaluationModal(false);
    setSelectedGuard(null);
  };

  const handleViewPerformance = (guardId: string) => {
    setShowPerformanceDetails(guardId);
  };

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A+':
      case 'A':
        return 'bg-green-100 text-green-800';
      case 'B+':
      case 'B':
        return 'bg-blue-100 text-blue-800';
      case 'C+':
      case 'C':
        return 'bg-yellow-100 text-yellow-800';
      case 'D':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-red-100 text-red-800';
    }
  };

  const getCurrentSemester = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentSemester = currentDate.getMonth() >= 6 ? 'H2' : 'H1';
    return `${currentSemester} ${currentYear}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Αξιολόγηση Φυλάκων OVIT Security</h2>
          <p className="text-muted-foreground">
            Δείτε την απόδοση και αξιολογήστε τους φύλακες της OVIT που εργάζονται στις εγκαταστάσεις σας
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge variant="outline" className="gap-2">
              <Calendar className="h-3 w-3" />
              Περίοδος Αξιολόγησης: {getCurrentSemester()}
            </Badge>
            <Badge variant="secondary" className="gap-2">
              <Shield className="h-3 w-3" />
              OVIT Security Services
            </Badge>
          </div>
        </div>
      </div>

      <div className="grid gap-4">
        {assignedGuards?.map((guard) => (
          <Card key={guard.guard_id} className="hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-full">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{guard.guard_name}</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Φύλακας OVIT • Ομάδα: {guard.team_name}
                  </p>
                  <p className="text-xs text-blue-600">
                    Εργάζεται στις εγκαταστάσεις σας
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={getGradeColor(guard.performance_grade)}>
                  Grade: {guard.performance_grade}
                </Badge>
                <Badge variant="secondary">
                  Score: {guard.final_score.toFixed(1)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    {guard.final_score.toFixed(1)}
                  </div>
                  <div className="text-xs text-muted-foreground">Final Score</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {guard.base_score.toFixed(1)}
                  </div>
                  <div className="text-xs text-muted-foreground">Base Score</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold">
                    {guard.hasEvaluation ? (
                      <div className="flex items-center justify-center gap-1 text-green-600">
                        <Star className="h-4 w-4 fill-current" />
                        Evaluated
                      </div>
                    ) : (
                      <span className="text-orange-600">Pending</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Αξιολόγηση Πελάτη
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-purple-600">
                    {getCurrentSemester()}
                  </div>
                  <div className="text-xs text-muted-foreground">Period</div>
                </div>
              </div>

              {guard.hasEvaluation && guard.currentEvaluation && (
                <div className="bg-muted/50 rounded-lg p-3">
                  <div className="text-sm font-medium mb-2">Your Evaluation Summary:</div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>Overall: {guard.currentEvaluation.overall_satisfaction}/5</div>
                    <div>Reliability: {guard.currentEvaluation.reliability_score}/5</div>
                    <div>Professionalism: {guard.currentEvaluation.professionalism_score}/5</div>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    Submitted: {format(new Date(guard.currentEvaluation.submitted_at), 'MMM dd, yyyy')}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewPerformance(guard.guard_id)}
                  className="gap-2"
                >
                  <Eye className="h-4 w-4" />
                  Προβολή Αναφοράς Απόδοσης
                </Button>
                
                <Button
                  size="sm"
                  onClick={() => handleEvaluateGuard(guard.guard_id, guard.team_id!)}
                  variant={guard.hasEvaluation ? "outline" : "default"}
                  className="gap-2"
                >
                  <Edit className="h-4 w-4" />
                  {guard.hasEvaluation ? "Επεξεργασία Αξιολόγησης" : "Αξιολόγηση Φύλακα OVIT"}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {(!assignedGuards || assignedGuards.length === 0) && (
          <Card>
            <CardContent className="text-center py-12">
              <Award className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Guards Assigned</h3>
              <p className="text-muted-foreground">
                No security guards are currently assigned to your teams.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Evaluation Modal */}
      {selectedGuard && (
        <ClientEvaluationModal
          open={showEvaluationModal}
          onOpenChange={(open) => {
            if (!open) {
              handleEvaluationComplete();
            } else {
              setShowEvaluationModal(open);
            }
          }}
          guardId={selectedGuard.guardId}
          guardName={selectedGuard.guardName}
          guardAvatar={selectedGuard.guardAvatar}
          teamId={selectedGuard.teamId}
        />
      )}

      {/* Performance Details Modal */}
      {showPerformanceDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">Performance Report</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPerformanceDetails(null)}
              >
                ✕
              </Button>
            </div>
            <div className="p-6">
              <PerformanceBreakdownView 
                guardId={showPerformanceDetails} 
                guardName={assignedGuards?.find(g => g.guard_id === showPerformanceDetails)?.guard_name || ""}
                teamId={assignedGuards?.find(g => g.guard_id === showPerformanceDetails)?.team_id}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};